import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { IconComponent } from '../icon/icon.component';
import { BreadcrumbService } from '../../services/breadcrumb.service';

/** Sticky page headers below the breadcrumb read this to offset their own `top`, e.g. `top: var(--mb-breadcrumb-height, 0px)`. */
const BREADCRUMB_HEIGHT_VAR = '--mb-breadcrumb-height';

@Component({
  selector: 'mindbase-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, IconComponent],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BreadcrumbComponent implements AfterViewInit, OnDestroy {
  private readonly breadcrumbService = inject(BreadcrumbService);
  readonly breadcrumbs$ = this.breadcrumbService.breadcrumbs$;

  /**
   * `mindbase-breadcrumb` is mounted once in the app shell and never destroyed on route
   * changes, so the resize observer must track a stable, always-rendered wrapper rather than
   * the component host — an unstyled host defaults to `display: inline`, whose box never
   * reports a size change, which froze `--mb-breadcrumb-height` at whatever the first page
   * in the session happened to render.
   */
  @ViewChild('heightProbe', { static: true }) private readonly heightProbeRef!: ElementRef<HTMLElement>;

  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.syncHeightVar());
    this.resizeObserver.observe(this.heightProbeRef.nativeElement);
    this.syncHeightVar();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    document.documentElement.style.setProperty(BREADCRUMB_HEIGHT_VAR, '0px');
  }

  private syncHeightVar(): void {
    const nav = this.heightProbeRef.nativeElement.querySelector<HTMLElement>('.mb-breadcrumb');
    const height = nav?.offsetHeight ?? 0;
    document.documentElement.style.setProperty(BREADCRUMB_HEIGHT_VAR, `${height}px`);
  }
}
